// import { BaseResponse }from '@/types/common';
import axiosInstance from './axiosInstance';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/**
 * API响应格式
 */
export interface ApiResponse<T> {
  /** 状态码 */
  status?: number;
  /** 是否成功 */
  success?: boolean;
  /** 消息 */
  message?: string;
  /** 数据 */
  data?: T;
}

/**
 * 抽奖活动信息
 */
export interface LottoList {
  /** ID */
  id?: number;
  /** 活动名称 */
  activityName?: string;
  /** 活动开始时间 */
  startTime?: string;
  /** 活动结束时间 */
  endTime?: string;
  /** 活动状态；0未开始；1进行中；2已结束 */
  activityStatus?: number;
  /** 抽奖券设置 */
  gashaponNum?: number;
  /** 活动类型；0扭蛋券抽奖；1盲盒抽奖 */
  type?: number;
  /** 活动是否上架；0已下架；1已上架 */
  status?: number;
}

/**
 * 抽奖活动列表响应
 */
export interface LottoResponse {
  /** 总数 */
  total?: number;
  /** 当前页 */
  currentPage?: number;
  /** 每页数量 */
  pageSize?: number;
  /** 记录列表 */
  records?: LottoList[];
}

/**
 * 抽奖活动列表查询参数
 */
export interface LottoListParams {
  /** 查询关键词 */
  keyword?: string;
  /** 活动状态；0未开始；1进行中；2已结束 */
  activityStatus?: number;
  /** 当前页 */
  currentPage?: number;
  /** 每页数量 */
  pageSize?: number;
}

/**
 * 获取抽奖活动列表
 * @param params - 查询参数
 * @returns Promise<ApiResponse<LottoResponse>>
 */
export const getLottoList = async (params: LottoListParams) => {
  const res = await axiosInstance.get<ApiResponse<LottoResponse>>(
    `${baseUrl}/lotteryActivityConfig/v1/list`,
    {
      params,
    }
  );
  return res.data;
};

/**
 * 更新抽奖活动状态（投放或停用）
 * @param id 活动 ID
 * @param status 状态值
 * @returns Promise<ApiResponse<any>>
 */
export const updateLottoStatus = async (id: number, status: number) => {
  const res = await axiosInstance.get<ApiResponse<any>>(
    `${baseUrl}/lotteryActivityConfig/v1/updateStatus`,
    {
      params: {
        id,
        status,
      },
    }
  );
  return res.data;
};

/**
 * 抽奖活动新增/编辑
 * @param data 活动参数
 * @returns Promise<ApiResponse<any>>
 */
export interface PrizeItem {
  prizeType: number;
  prizeName: string;
  prizeImage: string;
  winLimit: number;
}

export interface PrizePoolItem {
  gashaponNum: number;
  prizePoolName: string;
  prizeList: PrizeItem[];
}

export interface AddOrEditLottoParams {
  id?: number;
  activityName: string;
  startTime: string;
  endTime: string;
  applicableUser: number;
  activityRule: string;
  activityDesc: string;
  disclaimer: string;
  remark: string;
  maxWinTimes: number;
  dailyPartake: number;
  status: number;
  type: number;
  prizePoolList: PrizePoolItem[];
}

export const addOrEditLottoActivity = async (data: AddOrEditLottoParams) => {
  const res = await axiosInstance.post<ApiResponse<any>>(
    `${baseUrl}/lotteryActivityConfig/v1/addOrEdit`,
    data
  );
  return res.data;
};

export interface BlacklistListParams {
  keyword?: string; // 奖品名称/用户Id/用户手机号/昵称模糊搜索
  currentPage: number; // 当前页
  pageSize: number; // 页码大小
  startTime?: string; // 开始时间
  endTime?: string; // 结束时间
  status?: number; // 状态 0未封禁 1禁用
  operatorName?: number; // 操作人 模糊匹配
}

export interface BlacklistRecord {
  requestTime: string;
  id: number;
  userId: string;
  suppId: string;
  requestIp: string;
  deviceId: string;
  status: number; // 状态 0未封禁 1禁用
  bannedType: number; // 封禁类型 0用户id 1请求ip 2设备id
  operatorName: string;
}

export interface BlacklistListResponse {
  total: number;
  currentPage: number;
  pageSize: number;
  records: BlacklistRecord[];
}

/**
 * 获取抽奖黑名单列表
 * @param params 查询参数
 * @returns Promise<ApiResponse<BlacklistListResponse>>
 */
export const getBlacklistList = async (params: BlacklistListParams) => {
  const res = await axiosInstance.post<ApiResponse<BlacklistListResponse>>(
    `${baseUrl}/lotteryBlacklist/v1/list`,
    params
  );
  return res.data;
};

/**
 * 新增或编辑抽奖黑名单
 */
export interface AddOrEditBlacklistParams {
  id?: number;
  status: number;
  bannedType: number;
  userInfo: string;
}

export const addOrEditBlacklist = async (data: AddOrEditBlacklistParams) => {
  const res = await axiosInstance.post<ApiResponse<any>>(
    `${baseUrl}/lotteryBlacklist/v1/addOrEdit`,
    data
  );
  return res.data;
};

/**
 * 奖品信息
 */
export interface Prize {
  id: number;
  prizeName: string;
  prizeType?: string;
  totalStock?: number;
  remainStock?: number;
  winProbability?: number;
}

/**
 * 奖池信息
 */
export interface PrizePool {
  id: number;
  prizePoolName: string;
  gashaponNum: string;
  prizeList: Prize[];
}

/**
 * 获取奖池列表
 * @param activityId - 活动 ID
 * @returns Promise<ApiResponse<PrizePool[]>>
 */
export const getPrizeList = async (activityId: number) => {
  const res = await axiosInstance.get<ApiResponse<PrizePool[]>>(
    `${baseUrl}/prizeManagement/v1/getPrizeList`,
    {
      params: {
        activityId,
      },
    }
  );
  return res.data;
};

/**
 * 单个奖品信息
 */
export interface BatchPrize {
  id: number;
  totalStock: number;
  remainStock?: number;
  winProbability: number;
}

/**
 * 奖池更新参数
 */
export interface BatchUpdatePrize {
  id?: number;
  prizePoolName?: string;
  prizeList?: BatchPrize[];
}

/**
 * 批量更新奖品库存和概率
 * @param data 奖池列表
 * @returns Promise<ApiResponse<any>>
 */
export const batchUpdatePrize = async (
  data: BatchUpdatePrize[]
): Promise<ApiResponse<any>> => {
  const res = await axiosInstance.post<ApiResponse<any>>(
    `${baseUrl}/prizeManagement/v1/batchUpdatePrize`,
    data
  );
  return res.data;
};

/**
 * 获取抽奖活动详情参数
 */
export interface LottoDetailParams {
  id: string;
}

/**
 * 获取抽奖活动详情返回值
 */
export interface LottoDetailResponse {
  id: number;
  activityName: string;
  startTime: string;
  endTime: string;
  applicableUser: number;
  activityRule: string;
  activityDesc: string;
  disclaimer: string;
  remark: string;
  maxWinTimes: number;
  dailyPartake: number;
  status: number;
  type: number;
  prizePoolList: {
    id: number;
    gashaponNum: number;
    prizePoolName: string;
    prizeList: {
      id: number;
      prizeType: number;
      prizeName: string;
      prizeImage: string;
      winLimit: number;
    }[];
  }[];
}

/**
 * 获取抽奖活动详情
 * @param params 查询参数
 * @returns Promise<ApiResponse<LottoDetailResponse>>
 */
export const getLottoDetail = async (params: LottoDetailParams) => {
  const res = await axiosInstance.get<ApiResponse<LottoDetailResponse>>(
    `${baseUrl}/lotteryActivityConfig/v1/detail`,
    {
      params,
    }
  );
  return res.data;
};

export interface WinningRecordParams {
  keyword?: string; // 奖品名称/用户Id/用户手机号/昵称 模糊搜索
  currentPage: number;
  pageSize: number;
  startTime?: string;
  endTime?: string;
  prizeType?: number; // 奖品类型（1: 实物, 2: 虚拟产品）
  prizeStatus?: number; // 奖品状态（1: 未领取, 2: 已领取）
  activityId?: number;
}

export interface WinningRecord {
  id: number;
  grantTime: string;
  prizeName: string;
  prizeStatus: number;
  userId: string;
  prizeType: number;
  userName: string;
  phoneNumber: string;
  logisticsCompany: string;
  waybillNumber: string;
  userAddrId: number;
  receiveAddress: {
    id: number;
    userId: number;
    fullName: string;
    fullNameInFurigana: string;
    postalCode: string;
    baseAddress: string;
    detailAddress: string;
    phoneNumber: string;
    email: string;
  };
}

export interface WinningRecordListResponse {
  total: number;
  currentPage: number;
  pageSize: number;
  records: WinningRecord[];
}

/**
 * 获取中奖记录列表
 * @param params 查询参数
 * @returns Promise<ApiResponse<WinningRecordListResponse>>
 */
export const getWinningRecordList = async (params: WinningRecordParams) => {
  const res = await axiosInstance.post<ApiResponse<WinningRecordListResponse>>(
    `${baseUrl}/prizeManagement/v1/list`,
    params
  );
  return res.data;
};

export interface ReceiveAddress {
  id: number;
  userId: number;
  fullName: string;
  fullNameInFurigana: string;
  postalCode: string;
  baseAddress: string;
  detailAddress: string;
  phoneNumber: string;
  email: string;
}

export interface PrizeDetailResponse {
  id: number;
  grantTime: string;
  prizeName: string;
  prizeStatus: number;
  userId: string;
  prizeType: number;
  userName: string;
  phoneNumber: string;
  logisticsCompany: string;
  waybillNumber: string;
  userAddrId: number;
  receiveAddress: ReceiveAddress;
}

/**
 * 获取奖品详情
 * @param id 奖品记录ID
 * @returns Promise<ApiResponse<PrizeDetailResponse>>
 */
export const getPrizeDetail = async (id: number) => {
  const res = await axiosInstance.get<ApiResponse<PrizeDetailResponse>>(
    `${baseUrl}/prizeManagement/v1/detail/${id}`
  );
  return res.data;
};

/**
 * 修改奖品状态为“已领取”
 * @param id 奖品记录ID
 * @returns Promise<ApiResponse<any>>
 */
export const updatePrizeStatusToReceived = async (id: number) => {
  const res = await axiosInstance.post<ApiResponse<any>>(
    `${baseUrl}/prizeManagement/v1/status/update/${id}`
  );
  return res.data;
};

export interface LogisticsInfo {
  id?: number; // 中奖主键id
  logisticsCompany?: string; // 物流公司
  waybillNumber?: string; // 运单号
}

/**
 * 保存物流信息
 * @param data 物流信息对象
 * @returns Promise<ApiResponse<any>>
 */
export const updateLogisticsInfo = async (data: LogisticsInfo) => {
  const res = await axiosInstance.post<ApiResponse<any>>(
    `${baseUrl}/prizeManagement/v1/logistics/update`,
    data
  );
  return res.data;
};

export interface PrizeStatistics {
  issued: number; // 已发放奖品数量
  received: number; // 已领取奖品数量
  unclaimed: number; // 未领取奖品数量
}

/**
 * 获取抽奖活动的奖品统计数据
 * @param activityId 活动ID
 * @returns Promise<ApiResponse<PrizeStatistics>>
 */
export const getPrizeStatistics = async (
  activityId: number
): Promise<ApiResponse<PrizeStatistics>> => {
  const res = await axiosInstance.get<ApiResponse<PrizeStatistics>>(
    `${baseUrl}/prizeManagement/v1/statistics/${activityId}`
  );
  return res.data;
};
